import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable } from 'rxjs/internal/Observable';

import { MediaFile } from '@app/models';

export interface MediaFileApi {
  items: MediaFile[];
  total_count: number;
}

@Injectable({
  providedIn: 'root'
})
export class MaterialService {
  private _materialsRoute = '/api/materials/';
  private headerOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

  private currentMaterialBeingViewed: MediaFile;
  private materialToEdit: MediaFile;

  constructor(private httpClient: HttpClient) { }

  setCurrentMaterialBeingViewed(_media: MediaFile): void {
    this.currentMaterialBeingViewed = _media;
  }

  getCurrentMaterialBeingViewed(): MediaFile {
    return this.currentMaterialBeingViewed;
  }

  setMaterialToEdit(_media: MediaFile): void {
    this.materialToEdit = _media;
  }

  getMaterialToEdit(): MediaFile {
    return this.materialToEdit;
  }


  hasMaterialPermission(_user) {
    if (!_user || _user === undefined || !_user.userType || _user.userType === undefined) return false;

    const allowedTypes: string[] = ['system admin', 'super admin'];
    const userType = _user?.userType?.toLowerCase()?.trim();
    return allowedTypes.includes(userType);
  }


  getMedia(): Observable<any[]> {
    return this.httpClient.get<any[]>(this._materialsRoute);
  }


  getMediaNonDeleted(): Observable<any[]> {
    return this.httpClient.get<any[]>(this._materialsRoute + 'non-deleted');
  }


  getMediaById(_materialId: string): Observable<any> {
    return this.httpClient.get<any>(this._materialsRoute + _materialId);
  }


  getUsersPostedMedia(_userId: string): Observable<any[]> {
    return this.httpClient.get<any[]>(this._materialsRoute + 'user-posted-material/' + _userId);
  }


  getUsersFavoriteMaterials(_userId: string): Observable<any[]> {
    return this.httpClient.get<any[]>(this._materialsRoute + 'users-favorites/' + _userId);
  }


  getAllMaterialForList(): Observable<any[]> {
    return this.httpClient.get<any[]>(this._materialsRoute + 'allMaterialForListView/');
  }


  getFilteredMediaByPage(_curPage, _itemsPerPage): Observable<any[]> {
    return this.httpClient.get<any[]>(this._materialsRoute + 'media-filtered/page/' + _curPage + '/itemsPerPage/' + _itemsPerPage);
  }


  getMediaSearch(_searchParams: any): Observable<any> {
    return this.httpClient.post<any>(this._materialsRoute + 'search', JSON.stringify(_searchParams), this.headerOptions);
  }


  getMediaSearchByPage(_searchParams: any, _curPage, _itemsPerPage): Observable<any> {
    return this.httpClient.post<any>(this._materialsRoute + 'media-searched-media/page/' + _curPage + '/itemsPerPage/' + _itemsPerPage, JSON.stringify(_searchParams), this.headerOptions);
  }


  getCustomSearchByPage(_searchParams: any, _curPage, _itemsPerPage): Observable<any> {
    _searchParams.curpage = _curPage;
    _searchParams.itemsPerPage = _itemsPerPage;
    return this.httpClient.post<any>(this._materialsRoute + 'media-searched-media', JSON.stringify(_searchParams), this.headerOptions);
  }


  getRelatedMaterials(_searchParams: any, _curPage, _itemsPerPage): Observable<any> {
    return this.httpClient.post<any>(this._materialsRoute + 'relatedMaterials/page/' + _curPage + '/itemsPerPage/' + _itemsPerPage, JSON.stringify(_searchParams), this.headerOptions);
  }


  getFilteredMedia(_filter: any): Observable<any[]> {
    return this.httpClient.get<any[]>(this._materialsRoute + 'media-filtered/' + _filter);
  }


  saveMaterial(_mediaFile: MediaFile): Observable<any> {
    return this.httpClient.post<any>(this._materialsRoute + 'createMaterial', JSON.stringify(_mediaFile), this.headerOptions);
  }


  update(_mediaFile: MediaFile): Observable<any> {
    return this.httpClient.put<any>(this._materialsRoute + _mediaFile._id, JSON.stringify(_mediaFile), this.headerOptions);
  }


  markDelete(_id: string): Observable<any> {
    return this.httpClient.put(this._materialsRoute + 'mark-deleted/' + _id, this.headerOptions);
  }


  markDeletedByIdArray(_arrayOfIds): Observable<any> {
    return this.httpClient.post<any>(this._materialsRoute + 'mark-deleted/by-array', JSON.stringify(_arrayOfIds), this.headerOptions);
  }


  delete(_mediaFile: MediaFile): Observable<any> {
    return this.httpClient.put(this._materialsRoute + _mediaFile?._id, this.headerOptions);
  }


  search(searchTerms): Observable<MediaFile[]> {
    return this.httpClient.post<MediaFile[]>(this._materialsRoute + 'search', JSON.stringify(searchTerms), this.headerOptions);
  }


  selectAllSearch(searchTerms): Observable<any> {
    searchTerms['selectAll'] = true;
    return this.httpClient.post<any>(this._materialsRoute + 'dataSourceSearch', JSON.stringify(searchTerms), this.headerOptions);
  }


  getCustomDatasource(_searchParams, _sortField = 'random', _sortDirection = 'desc', _pageNumber: number = 0, _pageSize: number = 10): Observable<MediaFileApi> {
    _searchParams['selectAll'] = false;
    _searchParams.sortField = _sortField;
    _searchParams.sortOrder = (_sortDirection && _sortDirection !== '') ? _sortDirection : 'desc';
    _searchParams.pageNumber = _pageNumber;
    _searchParams.pageSize = _pageSize;

    return this.httpClient.post<MediaFileApi>(`${this._materialsRoute}dataSourceSearch`, JSON.stringify(_searchParams), this.headerOptions);
  }
}